<script lang="ts" setup>
import { watch, ref, onMounted, nextTick, onBeforeUnmount, Ref } from 'vue';
import { useRafFn } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { getElementFromEpubCFI } from '../util/location';
import useSettingStore from '../stores/setting';
import useReaderStore from '../stores/reader';
import useMediaStore from '../stores/media';
import { Track, NavigationDirection } from '../types';
import useRenditionStore from '../stores/rendition';
import { audioSlicePlayed } from '../util/reading-session';
import useReaderEngine from '../engine/use-reader-engine';
import useAudioTracks from '../composables/use-audio-tracks';
import useAudioAnnotations from '../composables/use-audio-annotations';
import useUiStore from '../stores/ui';
import useMediaSession, { MediaSessionMetadata } from '../util/use-media-session';
import useBackgroundPlaybackNavigation from '../composables/use-background-playback-navigation';
import { Capacitor } from '@capacitor/core';
import useNavigationStore from '../stores/navigation';
import useNavigationTriggerStore from '../stores/navigation-trigger';
import usePlaylistStore from '../stores/playlist';

type Emits = {
  (e: 'nextTrackOnPage', track: Track): void;
  (e: 'goToNextPlayListSegment'): void;
  (e: 'goToNextSpine'): void;
  (e: 'loadContinuousPlayback', track: Track): void;
  (e: 'loadNextSpineTracks', href: string): void;
  (e: 'seekToTime', time: number): void;
};
const emit = defineEmits<Emits>();

const settingsStore = useSettingStore();
const { hasAudioTracking, hasAutoPageTurn, playbackRate, isMuted, audioVolume } = storeToRefs(settingsStore);
const { isContinuous, isAudioOnly, bookAuthors, bookTitle, bookCoverUrl } = storeToRefs(useReaderStore());
const mediaStore = useMediaStore();
const {
  currentPageTracks,
  isPlaying,
  playing,
  currentTrack,
  getNextTrackOnPage,
  getUniqueNextTrackOnPage,
  mainLanguage,
  isPlayingMainLanguage,
  isVideoLanguage,
  pictureInPicture,
  audioLanguage,
  lastPlayedBeginTime,
  isCurrentClipOnCurrentPage,
  requestedPlayerPosition,
  currentPlayerTime,
  isLastTrack,
  isUserChangingTrack,
} = storeToRefs(mediaStore);
const {
  switchToTheNextPage,
  setFirstTrackInPageAsCurrent,
  setCurrentTrack,
  setLastPlayedBeginTime,
  setCurrentPlayerTime,
  setAudioDuration,
  getTracksByHref,
  setIsUserChangingTrack,
} = mediaStore;
const renditionStore = useRenditionStore();
const { isChangingPage, isReflowable, currentPageFilesWithTracks, rendition, isAtRenditionEnd } =
  storeToRefs(renditionStore);
const { currentPage, totalSpineItems, currentSpineIndex } = storeToRefs(useNavigationStore());
const { goToPage, clearCurrentTarget, setIsChangingPage, getCurrentTarget } = useRenditionStore();
const playlistStore = usePlaylistStore();
const { currentPlaylistItem } = storeToRefs(playlistStore);
const { setCurrentTime: setPlaylistCurrentTime } = playlistStore;
const { getSpineByIdentifier, getCFIFromSpineIdentifier, getPageSpineItem } = useReaderEngine();
const { highlightPlayingTrack, clearPlayingHighlight } = useAudioAnnotations();
const { isReaderVisible, animationDuration } = storeToRefs(useUiStore());
const { loadNextSpineTracksByHrefAndPlay, loadSpineTracksFromTrack } = useAudioTracks();
const {
  setMediaMetadata,
  registerPlayHandler,
  registerPauseHandler,
  registerSeekToHandler,
  registerSeekBackwardHandler,
  registerSeekForwardHandler,
  registerNextTrackHandler,
  registerPreviousTrackHandler,
  clearMediaSession,
  setPlayBackState,
  setPositionState,
} = useMediaSession();
const { jumpToTrackByOffset, goToNextForBackgroundPlayback, goToPreviousForBackgroundPlayback } =
  useBackgroundPlaybackNavigation();
const { setNavigationTrigger } = useNavigationTriggerStore();

const mediaPlayer = ref<HTMLAudioElement | null>(null);
const audioUrl = ref<string | undefined>(undefined);

// Post-animation breathing room for fixed layout books before narration resumes.
// Within a spread (right page already visible) needs less time than a brand new spread.
const FIXED_LAYOUT_WITHIN_SPREAD_DELAY_MS = 500;
const FIXED_LAYOUT_NEW_SPREAD_DELAY_MS = 1500;

const pageTracksBeforeBackgroundPlayback: Ref<Track[]> = ref([]);
const isReaderNewlyVisible = ref(false);
const isMediaSessionInitialized = ref(false);

// Guard flag to prevent double-triggering navigation from both timeupdate and ended events
const isProcessingTrackEnd = ref(false);
// Guard flag to prevent double-triggering goToNextSpine from rapid timeupdate events at turnPageAt
const isProcessingPageTurn = ref(false);

// Auto-advances onto a page with no audio, since the last track started.
const blindPageTurns = ref(0);
const MAX_BLIND_PAGE_TURNS = 10;

// Caching for timeupdate performance optimization
const cachedTrackIdentifier = ref<string | null>(null);
const cachedCumulativeTime = ref(0);

const recordAudioSlicePlayed = async (track: Track, endTime: number) => {
  if (!track.audio.clipBegin || !audioLanguage.value) {
    return;
  }

  try {
    const cfi = track.cfi || (await getCFIFromSpineIdentifier(track.text.src));
    audioSlicePlayed(track.audio.clipBegin, endTime, cfi, track.audio.src, audioLanguage.value);
  } catch (e) {
    console.log('Error recording audio slice played:', e);
  }
};

const setCurrentTrackTime = (trackTime: number) => {
  if (mediaPlayer.value) {
    mediaPlayer.value.currentTime = trackTime;
  }
};

const resetPlayerTime = () => {
  const clipBegin =
    currentTrack.value && currentTrack.value.audio && currentTrack.value.audio.clipBegin !== null
      ? currentTrack.value.audio.clipBegin
      : 0;
  setCurrentTrackTime(clipBegin);
};

const isAudioFileEnded = () =>
  currentTrack.value &&
  currentTrack.value.audio.clipEnd !== null &&
  mediaPlayer.value!.currentTime >= currentTrack.value.audio.clipEnd;

// Start the slide a lead ahead of the last clip's end, so it is over before the next page's
// narration begins. Media overlay audio usually has no silence between clips.
const isReflowablePageTurnDue = () => {
  if (!isReflowable.value || isContinuous.value || !hasAutoPageTurn.value || !isReaderVisible.value) {
    return false;
  }
  const clipEnd = currentTrack.value?.audio.clipEnd;
  if (!clipEnd || !mediaPlayer.value || getNextTrack()) {
    return false;
  }
  const clipLength = clipEnd - (currentTrack.value!.audio.clipBegin ?? 0);
  // Page-number markers run well under a second, so never eat more than half a clip.
  const lead = Math.min(animationDuration.value * 1.1, clipLength / 2);
  return mediaPlayer.value.currentTime >= clipEnd - lead;
};

const isAutoPageTurnPointReached = () =>
  currentTrack.value &&
  currentTrack.value.audio.turnPageAt &&
  mediaPlayer.value &&
  mediaPlayer.value.currentTime >= currentTrack.value.audio.turnPageAt &&
  hasAutoPageTurn.value;

const play = () => {
  if (mediaPlayer.value) {
    mediaPlayer.value.play().catch((error: any) => {
      console.error(error);
    });
    setPlayBackState(true);
  }
};

const pause = () => {
  if (mediaPlayer.value) {
    mediaPlayer.value.pause();
    setPlayBackState(false);
  }
};

const togglePlay = (toggle: boolean) => {
  if (!toggle) {
    console.debug(`Pausing media`);
    pause();
    return;
  }

  if (!currentTrack.value) {
    switchToTheNextPage();
    return;
  }

  console.debug(`Playing media`);
  play();

  if (requestedPlayerPosition.value) {
    setCurrentTrackTime(requestedPlayerPosition.value);
    mediaStore.resetRequestedPlayerPosition();
  }
};

// Single owner of narration resumption after page animation completes for all paginated book types.
// Continuous books keep immediate resume via the else branch in onCurrentTrackChange.
watch(isChangingPage, (changing, wasChanging) => {
  if (!changing && wasChanging && isPlaying.value && currentTrack.value && !isContinuous.value) {
    if (isReflowable.value) {
      togglePlay(true);
      return;
    }

    // Fixed layout: give users time to see the new spread before narration resumes.
    // Even spine indices are the right page of an existing spread (shorter pause);
    // odd indices (including 0) open a brand-new spread (longer pause).
    const isWithinSpread = currentSpineIndex.value > 0 && currentSpineIndex.value % 2 === 0;
    const delay = isWithinSpread ? FIXED_LAYOUT_WITHIN_SPREAD_DELAY_MS : FIXED_LAYOUT_NEW_SPREAD_DELAY_MS;
    setTimeout(() => {
      if (isPlaying.value) togglePlay(true);
    }, delay);
  }
});

const navigateToTrackPage = (track: Track): void => {
  goToPage({
    target: track.text.src,
    isGoingForwardsInBook: true,
  });
  // Reset flag, that was set in goToPage method.
  setIsChangingPage(false);
};

/**
 * Determines the next track to play based on language and video settings
 */
const getNextTrack = () => {
  if (isPlayingMainLanguage.value) {
    return getNextTrackOnPage.value;
  } else if (!isVideoLanguage.value || pictureInPicture.value.length !== 0) {
    return getUniqueNextTrackOnPage.value;
  }
  return null;
};

const onTrackFinished = () => {
  if (isProcessingTrackEnd.value) {
    console.debug('Already processing track end, skipping duplicate call');
    return;
  }

  isProcessingTrackEnd.value = true;

  try {
    // Figure out the next track immediately.
    const nextTrackOnPage = getNextTrack();

    // Re-entrancy guard: reflowable cannot hold isProcessingPageTurn across a turn, so without
    // this the RAF loop queues another turn every ~110ms and skips pages. Bail before
    // recordAudioSlicePlayed so a blocked retry is not counted twice.
    if (!nextTrackOnPage && isChangingPage.value && hasAutoPageTurn.value && isReaderVisible.value) {
      return;
    }

    // If it is Fixed Layout, pause instantly so it doesn't bleed.
    // If it is Reflowable, DO NOT PAUSE! Let the single MP4 naturally play into the
    // next sentence on the next page gaplessly while the UI animation runs!
    if (!nextTrackOnPage && hasAutoPageTurn.value && isReaderVisible.value && !isAudioOnly.value) {
      if (!isReflowable.value && !isContinuous.value) {
        mediaPlayer.value?.pause();
      }
    }

    // FIRE AND FORGET: Do not await this!
    // Calculating the CFI blocks the main thread. We let it run in the background
    // so it doesn't freeze the audio buffer during the page transition.
    if (currentTrack.value) {
      recordAudioSlicePlayed(currentTrack.value, currentPlayerTime.value).catch((e) => console.log(e));
    }

    // Audio-only mode: Check for next track in current playlist item first
    // Only jump to next playlist segment when all tracks in current item are done
    if (isAudioOnly.value) {
      if (nextTrackOnPage) {
        console.debug('Going to next track in current playlist item');
        setCurrentTrack(nextTrackOnPage);
        emit('nextTrackOnPage', nextTrackOnPage);
      } else {
        console.debug('All tracks finished in playlist item, triggering next playlist segment');
        setNavigationTrigger('AUTOMATIC_TRACK_ADVANCE');
        emit('goToNextPlayListSegment');
      }
      return;
    }

    if (nextTrackOnPage) {
      // Move to the next track on the current page.
      // (Because we didn't call pause() above, native gapless playback continues smoothly!)
      console.debug('Going to next track on page');
      setCurrentTrack(nextTrackOnPage);
      emit('nextTrackOnPage', nextTrackOnPage);
    } else if (hasAutoPageTurn.value && isReaderVisible.value) {
      // Without this, the loop-back branch below replays the last page forever.
      if (isAtRenditionEnd.value) {
        console.debug('End of book reached, stopping playback');
        pause();
        return;
      }

      if (blindPageTurns.value >= MAX_BLIND_PAGE_TURNS) {
        console.debug(`Stopped after ${MAX_BLIND_PAGE_TURNS} page turns without audio`);
        pause();
        return;
      }
      blindPageTurns.value += 1;

      // Auto-advance to the next spine when the reader is visible
      console.debug(`No more media tracks on current page(s), going to next spine`);

      // Fixed layout: set the flag so the RAF loop won't re-call onTrackFinished() while
      // the doWithAutoPageTurnTimeout 2s delay is counting down (audio stays at clipEnd,
      // so isAudioFileEnded() keeps returning true every ~100ms after isProcessingTrackEnd resets).
      // Reflowable: leave the flag alone — the reflowable bail-out guard in watch(currentPageFilesWithTracks)
      // must NOT fire for this path or the new page's first track won't be set.
      if (!isReflowable.value && !isContinuous.value) {
        isProcessingPageTurn.value = true;
      } else {
        isProcessingPageTurn.value = false;
      }
      setNavigationTrigger('AUTOMATIC_TRACK_ADVANCE');
      emit('goToNextSpine');
    } else if (!isReaderVisible.value && currentTrack.value) {
      // Background playback handling
      mediaPlayer.value?.pause();
      emit('loadContinuousPlayback', currentTrack.value);
    } else if (isPlaying.value) {
      // Loop back to the first track on the page
      console.debug(`Continue playing: ${isPlaying.value}`);
      setCurrentTrack(currentPageTracks.value[0]);
      resetPlayerTime();
      setLastPlayedBeginTime(currentPageTracks.value[currentPageTracks.value.length - 1].audio.clipBegin || 0);
      playing.value = !isPlaying.value;
    }
  } finally {
    setTimeout(() => {
      isProcessingTrackEnd.value = false;
    }, 100);
  }
};

// Fallback for when the whole audio file ends (tracks with null clipEnd).
// Normal clip-end detection happens in the RAF loop at ~16ms precision.
const onTrackEnd = () => {
  console.debug('Audio file ended');
  nextTick(() => {
    if (!isReaderVisible.value && currentPage.value?.href && !isContinuous.value) {
      loadNextSpineTracksByHrefAndPlay(currentPage.value.href);
    }
    if (!isChangingPage.value && (isReaderVisible.value || isContinuous.value)) {
      onTrackFinished();
    }
  });
};

const clearCurrentTrack = () => {
  if (mediaPlayer.value) {
    mediaPlayer.value.pause();
  }
  setCurrentTrack(undefined);
};

/**
 * Watcher to detect page switching.
 */
watch(currentPageFilesWithTracks, async () => {
  if (isReaderNewlyVisible.value) {
    console.debug('App was recently in background, continue playing current track');
    isReaderNewlyVisible.value = false;
    return;
  }

  if (currentPageTracks.value?.length > 0 && !isLastTrack.value) {
    console.debug(`${currentPageTracks.value.length} track(s) detected`);
    // Cross-page sentence: the same logical clip appears at the top of the new page because
    // it spans the page break. isCurrentClipOnCurrentPage uses src+clipBegin+clipEnd (not
    // turnPageAt) so it correctly matches the page-A and page-B versions of the same clip.
    // Update currentTrack to the page-B version (no turnPageAt) so isAutoPageTurnPointReached
    // won't re-trigger when audio resumes. Audio stays paused at turnPageAt; the top-level
    // watch(isChangingPage) resumes playback once the animation finishes.
    if (isChangingPage.value && isCurrentClipOnCurrentPage.value) {
      setFirstTrackInPageAsCurrent();
      return;
    }

    // Reflowable-specific guard: for auto-page-turns triggered by turnPageAt, the relocated
    // event fires before the scroll animation completes. The cross-page sentence element may
    // still appear as outside-viewport mid-scroll, causing isCurrentClipOnCurrentPage to be
    // false even though the sentence WILL be visible once scrolling settles. Calling
    // resetPlayerTime() here would snap audio back to clipBegin and replay the sentence.
    // Instead, bail out — isProcessingPageTurn prevents re-triggering goToNextSpine, and
    // watch(isChangingPage) resumes narration from the paused turnPageAt position.
    if (isChangingPage.value && isReflowable.value && isProcessingPageTurn.value) {
      return;
    }

    if (!isCurrentClipOnCurrentPage.value) {
      const target = getCurrentTarget();
      if (target) {
        try {
          const section = getSpineByIdentifier(target);
          const cfi = await getCFIFromSpineIdentifier(target);
          const id = getElementFromEpubCFI(cfi);
          const tracks = getTracksByHref(section.href, id);
          if (tracks && tracks.length) {
            clearCurrentTarget();
            setCurrentTrack(tracks[0]);
            return;
          }
        } catch (error) {
          console.debug('Could not resolve tracks for navigation target, falling back to current page.', error);
        }
      }

      setFirstTrackInPageAsCurrent();

      // Prevent micro-rewinds (stutters) if the native player naturally coasted.
      const currentClipBegin = currentTrack.value?.audio?.clipBegin || 0;
      const currentClipEnd = currentTrack.value?.audio?.clipEnd || Infinity;
      const currentNativeTime = mediaPlayer.value?.currentTime || 0;

      // Tightened to 0.25s (250ms) to prevent false positives on manual seeks
      const isPlayingGaplessly =
        mediaPlayer.value &&
        !mediaPlayer.value.paused &&
        currentNativeTime >= currentClipBegin - 0.25 &&
        currentNativeTime <= currentClipEnd + 0.25;

      if (!isPlayingGaplessly) {
        console.debug('Gapless failed or not applicable. Resetting time.');
        resetPlayerTime();
      } else {
        console.debug('Gapless playback maintained across page turn, skipping rewind.');
      }
    }
  } else {
    clearCurrentTrack();
    if (isPlaying.value) {
      switchToTheNextPage();
    }
  }
});

// RAF loop: runs at ~60fps while audio is playing.
// Handles both clipEnd detection (~16ms vs old 250ms timeupdate) and turnPageAt.
const { pause: pauseRaf, resume: resumeRaf } = useRafFn(
  () => {
    if (!mediaPlayer.value) return;

    const currentTime = mediaPlayer.value.currentTime ?? 0;
    setCurrentPlayerTime(currentTime);

    // Update playlist time for audio-only mode
    if (isAudioOnly.value && currentTrack.value && currentPlaylistItem.value) {
      // Calculate time into the current track
      const clipBegin = currentTrack.value.audio.clipBegin ?? 0;
      const timeIntoCurrentTrack = Math.max(0, currentTime - clipBegin);

      const currentIdentifier = `${currentTrack.value.audio.src}:${currentTrack.value.text.src}`;

      // Safe performance optimization: only recalculate the loop when the track changes
      if (cachedTrackIdentifier.value !== currentIdentifier) {
        // Find the current track's index within the playlist item
        const trackIndexInItem = currentPlaylistItem.value.tracks.findIndex(
          (track) =>
            track.audio.src === currentTrack.value?.audio.src && track.text.src === currentTrack.value?.text.src,
        );

        // Calculate the cumulative time of all previous tracks in this playlist item
        let timeFromPreviousTracks = 0;
        if (trackIndexInItem > 0) {
          for (let i = 0; i < trackIndexInItem; i++) {
            const track = currentPlaylistItem.value.tracks[i];
            const trackDuration = (track.audio.clipEnd ?? 0) - (track.audio.clipBegin ?? 0);
            timeFromPreviousTracks += Math.max(0, trackDuration);
          }
        }

        cachedCumulativeTime.value = timeFromPreviousTracks;
        cachedTrackIdentifier.value = currentIdentifier;
      }

      // Total playlist time = playlist item start + previous tracks + current position
      const playlistTime = currentPlaylistItem.value.startTime + cachedCumulativeTime.value + timeIntoCurrentTrack;
      setPlaylistCurrentTime(playlistTime);
    }

    if (!currentTrack.value) return;

    // audio limits must be enforced even while page animations are happening.
    if (isAudioFileEnded() || isReflowablePageTurnDue()) {
      if (isPlayingMainLanguage.value && !isProcessingTrackEnd.value && !isProcessingPageTurn.value) {
        console.debug('Reached the end of the clip, switching to the next one.');
        onTrackFinished();
      }
    }

    // We only trigger new page turns if we aren't already actively turning the page
    else if (!isChangingPage.value && isAutoPageTurnPointReached() && isReaderVisible.value) {
      if (isProcessingPageTurn.value) return;
      isProcessingPageTurn.value = true;
      console.debug('Switching to the next spine to keep reading the media.');

      // Reflowable: no pause — audio continues seamlessly while the page scrolls.
      // Fixed layout: pause so audio doesn't play over the slide/flip animation.
      if (!isContinuous.value && !isReflowable.value) {
        pause();
      }
      setNavigationTrigger('AUTOMATIC_PAGE_TURN');
      emit('goToNextSpine');
    }
  },
  { immediate: false },
);

/**
 * Handler for current track changes.
 * Sets correct media URL and track start position.
 */
const onCurrentTrackChange = async (newTrack: Track, oldTrack?: Track) => {
  isProcessingTrackEnd.value = false;
  isProcessingPageTurn.value = false;
  blindPageTurns.value = 0;
  if (!oldTrack || (newTrack && newTrack.audio.src !== oldTrack.audio.src)) {
    audioUrl.value = newTrack.audio.url;
  }

  if (isUserChangingTrack.value) {
    playing.value = false;
  }

  await nextTick(async () => {
    if (newTrack && currentTrack.value) {
      // Only reset the current playback position if the track was changed by the user
      // (otherwise some part of the media might be played twice)
      if (isUserChangingTrack.value) {
        if (currentPlayerTime.value !== 0 && currentTrack.value) {
          await recordAudioSlicePlayed(currentTrack.value, currentPlayerTime.value);
        }
        if (
          lastPlayedBeginTime.value === currentTrack.value.audio.clipBegin &&
          currentTrack.value.audio.clipBegin !== null &&
          lastPlayedBeginTime.value !== null
        ) {
          // Sets the media player current time as clip end to avoid played media twice.
          setCurrentTrackTime(currentTrack.value.audio.clipEnd || 0);
        } else {
          // Sets the media player current time
          setCurrentTrackTime(currentTrack.value.audio.clipBegin || 0);
        }
        // in case of isUserChangingTrack we are triggering player in this way
        playing.value = true;
      }

      console.debug(`Current audio track ${newTrack.audio.url} starting at ${currentTrack.value.audio.clipBegin}`);

      if (isPlaying.value && !isUserChangingTrack.value && (isContinuous.value || !isChangingPage.value)) {
        togglePlay(isPlaying.value);
      }
      setIsUserChangingTrack(false);
    } else {
      console.debug('No track to play');
    }
  });
};

/**
 * Updates the page state when the app becomes visible after being hidden.
 * Ensures the reader is on the correct page for the currently playing track.
 * If the current track is not part of the last visible tracks, navigates to the track's page.
 */
const updatePageStateOnVisible = async () => {
  if (!currentTrack.value) return;

  const isCurrentTrackPartOfLastVisiblePages = pageTracksBeforeBackgroundPlayback.value.some(
    (track) => track.text.src === currentTrack.value?.text.src,
  );
  if (!isCurrentTrackPartOfLastVisiblePages) {
    navigateToTrackPage(currentTrack.value);
  }
};

/**
 * Seeks forward or backward by the specified amount of seconds, jumping across multiple tracks if needed.
 * @param seekAmount - The amount of seconds to seek. Defaults to 10 if null/undefined.
 * @param direction - Direction to seek ('next' for forward, 'prev' for backward).
 */
const seekByAmount = async (seekAmount: number | null | undefined, direction: NavigationDirection) => {
  if (!mediaPlayer.value || !currentTrack.value) return;

  const finalSeekAmount = seekAmount ?? 10;

  const currentTime = mediaPlayer.value.currentTime;
  const targetTime = direction === 'next' ? currentTime + finalSeekAmount : currentTime - finalSeekAmount;

  const clipBegin = currentTrack.value.audio.clipBegin ?? 0;
  const clipEnd = currentTrack.value.audio.clipEnd ?? mediaPlayer.value.duration;

  // Case 1: Stay within current track
  if (targetTime <= clipEnd && targetTime >= clipBegin) {
    mediaPlayer.value.currentTime = targetTime;
    setCurrentPlayerTime(targetTime);
    await setPositionState(mediaPlayer.value);
    return;
  }

  // Case 2: Need to go to next track(s)
  let remainingSeekTime = direction == 'next' ? targetTime - clipEnd : clipBegin - targetTime;
  let targetPosition = 0;
  while (remainingSeekTime > 0) {
    const bookHasMultipleSpines = totalSpineItems.value > 1;
    const jumpedToNewTrack = await jumpToTrackByOffset(1, direction, bookHasMultipleSpines);

    // If we hit a boundary (no more tracks), then start playing from the current track boundary.
    if (!jumpedToNewTrack || !currentTrack.value) {
      targetPosition =
        direction == 'next'
          ? (currentTrack.value.audio.clipEnd ?? mediaPlayer.value.duration)
          : (currentTrack.value.audio.clipBegin ?? 0);
      break;
    }

    const newTrackBegin = currentTrack.value.audio.clipBegin ?? 0;
    const newTrackEnd = currentTrack.value.audio.clipEnd ?? 0;
    const newTrackDuration = newTrackEnd - newTrackBegin;

    // Found the track we need to stay at.
    if (remainingSeekTime < newTrackDuration) {
      targetPosition = direction == 'next' ? newTrackBegin + remainingSeekTime : newTrackEnd - remainingSeekTime;
      remainingSeekTime = 0;
      break;
    }

    remainingSeekTime -= newTrackDuration;
  }

  mediaPlayer.value.currentTime = targetPosition;
  setCurrentPlayerTime(targetPosition);
  await setPositionState(mediaPlayer.value);
};

const setMediaSessionMetadata = () => {
  const title = bookTitle?.value ?? '';
  const authors = bookAuthors?.value ?? '';
  const coverUrl = bookCoverUrl?.value ?? '';
  const metadata: MediaSessionMetadata = { title: title, artist: authors, coverUrl: coverUrl };
  setMediaMetadata(metadata);
};

const setMediaSessionHandlers = () => {
  if (!mediaPlayer.value) return;

  registerPlayHandler(() => {
    if (!isUserChangingTrack.value) {
      playing.value = true;
      setPositionState(mediaPlayer.value);
    }
  });
  registerPauseHandler(() => {
    if (!isUserChangingTrack.value) {
      playing.value = false;
      setPositionState(mediaPlayer.value);
    }
  });

  registerSeekToHandler((details) => {
    if (!details.seekTime || !mediaPlayer.value) return;
    mediaPlayer.value.currentTime = details.seekTime;
    setCurrentPlayerTime(details.seekTime);
    setPositionState(mediaPlayer.value);
  });

  // Seek forward & seek backward won't show up on Android, therefore set next/prev track controls instead.
  if (Capacitor.getPlatform() == 'android') {
    registerNextTrackHandler(() => {
      goToNextForBackgroundPlayback();
      setPositionState(mediaPlayer.value);
    });
    registerPreviousTrackHandler(() => {
      goToPreviousForBackgroundPlayback();
      setPositionState(mediaPlayer.value);
    });
    return;
  }

  registerSeekForwardHandler((details) => seekByAmount(details.seekTime, 'next'));
  registerSeekBackwardHandler((details) => seekByAmount(details.seekTime, 'prev'));
};

//Initialize Media Session API when media becomes available
const initializeMediaSession = () => {
  setMediaSessionMetadata();
  setMediaSessionHandlers();
  isMediaSessionInitialized.value = true;
};

watch(
  [currentTrack, hasAudioTracking, isReaderVisible, mainLanguage, audioLanguage, isPlaying],
  ([track, tracking, visible, main, audio, playing]) => {
    if (!tracking || !visible || !rendition.value || isAudioOnly.value) {
      // Clear highlight if conditions not met
      if (mediaStore.activeHighlightCfi) {
        clearPlayingHighlight();
      }
      return;
    }

    // Apply highlight when playing, clear when paused
    if (track?.cfi && main === audio && playing) {
      highlightPlayingTrack(track);
    } else if (main === audio && !playing) {
      clearPlayingHighlight();
    }
  },
);

watch(audioUrl, () => {
  if (mediaPlayer.value!.readyState >= 2 || audioUrl.value !== mediaPlayer.value?.currentSrc) {
    console.debug('New audio file loaded - ', audioUrl.value);
    mediaPlayer.value?.load();
  }
});
/**
 * Apply audio player settings when they change
 */
watch([isMuted, playbackRate, audioVolume], ([muted, rate, volume]) => {
  if (!mediaPlayer.value) return;
  mediaPlayer.value.muted = muted;
  mediaPlayer.value.playbackRate = rate;
  if (volume !== undefined) {
    mediaPlayer.value.volume = volume;
  }
});

watch(isPlaying, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    togglePlay(newValue);
  }

  // Start/stop the RAF loop with playback state.
  if (newValue) {
    resumeRaf();
  } else {
    pauseRaf();
  }

  if (isPlaying.value && !isMediaSessionInitialized.value) {
    initializeMediaSession();
  }
});

/** Watcher to detect current media file switching. */
watch(currentTrack, (newTrack, oldTrack) => {
  if (newTrack && newTrack !== oldTrack) {
    onCurrentTrackChange(newTrack, oldTrack);
  }
});

// navigate within the audio track
watch(requestedPlayerPosition, () => {
  if (requestedPlayerPosition.value && isPlaying.value) {
    setCurrentPlayerTime(requestedPlayerPosition.value);
    mediaStore.resetRequestedPlayerPosition();
  }
});

watch(isReaderVisible, async (isVisible, wasVisible) => {
  // Background playback: find next page with audio tracks when current page has none
  if (!currentTrack.value && !isVisible && wasVisible && isPlaying.value && currentPage.value?.href) {
    // Cancel pending auto-page-turn since we're manually navigating to the correct page when the reader becomes visible.
    mediaStore.setNoMediaPageSkip(false);

    await loadNextSpineTracksByHrefAndPlay(currentPage.value.href);
    return;
  }

  //Navigate to the current track page if app becomes visible.
  if (isVisible && !wasVisible && currentTrack.value) {
    isReaderNewlyVisible.value = true;
    await updatePageStateOnVisible();
    return;
  }

  pageTracksBeforeBackgroundPlayback.value = currentPageTracks.value;

  //For background playback: If we only have one spine, load all tracks (needed for track navigation).
  if (wasVisible && !isVisible && currentTrack.value && totalSpineItems.value === 1) {
    await loadSpineTracksFromTrack(currentTrack.value);
  }
});

/**
 * Protect the currently playing chapter's iframe from being unloaded by the virtual scroller.
 * This ensures audio playback continues seamlessly even when the user scrolls to other chapters.
 */
watch([playing, currentTrack], async ([isPlaying, track]) => {
  // Only apply protection in continuous scrolling mode
  if (!isContinuous.value || !rendition.value) return;

  const manager = (rendition.value as any).manager;
  if (!manager || typeof manager.protectView !== 'function') return;

  if (isPlaying && track) {
    // Get the spine index from the current track
    try {
      const cfi = track.cfi || (await getCFIFromSpineIdentifier(track.text.src));
      const spineItem = getPageSpineItem(cfi);

      console.debug(`Protecting view for playing chapter at spine index ${spineItem.index}`);
      manager.protectView(spineItem.index);
    } catch (error) {
      console.error('Failed to protect view:', error);
    }
  } else {
    // Clear protection when playback stops
    console.debug('Clearing view protection (playback stopped)');
    manager.protectView(null);
  }
});

/**
 * Seek to a specific time in the current audio track
 * @param time - The target time in seconds
 * @param skipClamping - If true, don't clamp to track bounds
 */
const handleSeekToTime = async (time: number, skipClamping = false) => {
  if (!mediaPlayer.value || !currentTrack.value) return;

  let targetTime = time;

  if (!skipClamping) {
    const clipBegin = currentTrack.value.audio.clipBegin ?? 0;
    const clipEnd = currentTrack.value.audio.clipEnd ?? mediaPlayer.value.duration;
    targetTime = Math.max(clipBegin, Math.min(time, clipEnd));
  }

  mediaPlayer.value.currentTime = targetTime;
  setCurrentPlayerTime(targetTime);
  await setPositionState(mediaPlayer.value);
};

/**
 * Initializes audio player settings when the media can play
 */
const handleCanPlay = () => {
  if (mediaPlayer.value) {
    mediaPlayer.value.playbackRate = playbackRate.value;
    mediaPlayer.value.muted = isMuted.value;
    mediaPlayer.value.volume = audioVolume.value;
  }
};

/**
 * Updates MediaSession slider when new track metadata loads
 * Ensures the media player slider doesn't get stuck when the track is changed
 * Also updates the audio duration in the media store
 */
const handleLoadedMetadata = () => {
  if (mediaPlayer.value && isFinite(mediaPlayer.value.duration)) {
    setAudioDuration(mediaPlayer.value.duration);
  }
  setPositionState(mediaPlayer.value);
};

onMounted(() => {
  mediaPlayer.value?.addEventListener('canplay', handleCanPlay);
  mediaPlayer.value?.addEventListener('loadedmetadata', handleLoadedMetadata);
});

// Expose handleSeekToTime, seekByAmount and mediaPlayer for parent components.
defineExpose({
  handleSeekToTime,
  seekByAmount,
  mediaPlayer,
});

onBeforeUnmount(async () => {
  if (currentTrack.value && currentPlayerTime.value) {
    await recordAudioSlicePlayed(currentTrack.value, currentPlayerTime.value);
  }

  clearMediaSession();
  isMediaSessionInitialized.value = false;
});
</script>

<template>
  <!-- eslint-disable-next-line vuejs-accessibility/media-has-caption -->
  <audio ref="mediaPlayer" @ended="onTrackEnd">
    <source :src="audioUrl" type="audio/mpeg" />
    <source :src="audioUrl" />
  </audio>
</template>
